if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[id^="MediaDots"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Initialize dots functionality
        this.initializeDots();

        this.overrideSliderButtonLogic();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }
      
      overrideSliderButtonLogic() {
        if (!this.elements.viewer) return;
        
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');
        
        if (!slider || !prevButton || !nextButton) return;
        
        // Custom update function for product media gallery buttons
        const updateButtons = () => {
          const scrollLeft = slider.scrollLeft;
          const maxScroll = slider.scrollWidth - slider.clientWidth;
          
          if (scrollLeft <= 2) {
            prevButton.setAttribute('disabled', 'disabled');
            nextButton.removeAttribute('disabled');
          } 
          else if (scrollLeft >= maxScroll - 2) {
            nextButton.setAttribute('disabled', 'disabled');
            prevButton.removeAttribute('disabled');
          } 
          else {
            prevButton.removeAttribute('disabled');
            nextButton.removeAttribute('disabled');
          }
        };
        
        slider.addEventListener('scroll', updateButtons);
        
        updateButtons();
        
        // Update on resize
        window.addEventListener('resize', () => {
          setTimeout(updateButtons, 100);
        });
      }
      
      initializeDots() {
        if (!this.elements.dots || !this.elements.viewer) return;
        
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        const dots = this.elements.dots.querySelectorAll('.product-media-dot');
        
        if (!slider || dots.length === 0) return;
        
        // Function to update active dot
        const updateActiveDot = () => {
          const slides = slider.querySelectorAll('[id^="Slide-"]');
          const scrollLeft = slider.scrollLeft;
          const slideWidth = slides[0]?.offsetWidth || 0;
          
          if (slideWidth === 0) return;
          
          // Calculate current slide index
          const currentIndex = Math.round(scrollLeft / slideWidth);
          
          // Update dot states
          dots.forEach((dot, index) => {
            if (index === currentIndex) {
              dot.classList.add('product-media-dot--active');
            } else {
              dot.classList.remove('product-media-dot--active');
            }
          });
        };
        
        // Handle dot clicks
        dots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            const slides = slider.querySelectorAll('[id^="Slide-"]');
            if (slides[index]) {
              slider.scrollTo({
                left: slides[index].offsetLeft,
                behavior: 'smooth'
              });
            }
          });
        });
        
        // Update dots on scroll (without debounce for immediate feedback)
        slider.addEventListener('scroll', () => {
          updateActiveDot();
        });
        
        // Also update on resize
        window.addEventListener('resize', () => {
          updateActiveDot();
        });
        
        // Initial update
        updateActiveDot();
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;

        if (currentElement) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((slide) => {
            const active = slide === currentElement;
            slide.classList.toggle('is-active', active);
            slide.toggleAttribute('inert', !active);
            if (active) slide.removeAttribute('aria-hidden');
            else slide.setAttribute('aria-hidden', 'true');
          });
          this.elements.dots?.querySelectorAll('.product-media-dot').forEach((dot, index) => {
            if (index === event.detail.currentPage - 1) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }

        if (this.elements.thumbnails && currentElement?.dataset?.mediaId) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }

        const currentPage = event.detail.currentPage;
        const totalPages =
          this.elements.viewer?.totalPages ||
          Number(this.elements.viewer?.querySelector('.slider-counter--total')?.textContent) ||
          1;

        this.announceGallerySlidePosition(currentPage, totalPages);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        
        // Remove active class from all media items
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');


        
        this.preventStickyHeader();
        window.setTimeout(() => {
          // Scroll to the active media item
          const slider = activeMedia.parentElement;
          if (slider) {
            slider.scrollTo({ 
              left: activeMedia.offsetLeft, 
              behavior: 'smooth' 
            });
          }
          
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top <= -0.5) {
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceGallerySlidePosition(currentPage, totalPages) {
        if (!this.elements.liveRegion) return;
        const template = window.accessibilityStrings?.productMediaSlideChanged;
        if (!template) return;

        this.elements.liveRegion.removeAttribute('aria-hidden');
        const message = template
          .replace('[current]', String(currentPage))
          .replace('[total]', String(totalPages));

        this.elements.liveRegion.textContent = '';
        requestAnimationFrame(() => {
          this.elements.liveRegion.textContent = message;
        });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
